import React, { useState, useEffect, useMemo, useRef } from 'react';
import Loader from "react-loader-spinner";
import { FiX, FiCheck } from "react-icons/fi";
import { BASE_URL } from '../constants/config';
import {
    emptySettingsForm,
    settingsFormToPatch,
    isSettingsFormValid,
    settingsFormError,
} from '../utilities/seasonSettingsForm';
import SeasonSettingsFields from './SeasonSettingsFields';
import TeamPicker from './TeamPicker';
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import '../styles/editModal.css';
import '../styles/leagueManagement.css';

const resolveImage = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return BASE_URL.url + image;
};

// Приводим команду к единому виду: /leagues/:id/teams отдаёт team_id,
// а /teams/getAdminTeams - id
const normalizeTeam = (team) => ({
    team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
    name: team.name || team.team_name || 'Без названия',
    image: team.image || '',
    short_name: team.short_name || '',
});

export default function CreateSeasonModal({
    isOpen,
    onClose,
    onCreate,
    league,
    seasonsCatalog,
    resolveRoster,
    allLeagues,
    allTeams,
    allTeamsLoading,
    applications,
    onCreateApplication,
}) {
    const [step, setStep] = useState(1);
    const [seasonId, setSeasonId] = useState('');
    const [settingsForm, setSettingsForm] = useState(emptySettingsForm);

    const [sourceLeagueId, setSourceLeagueId] = useState('');
    const [sourceSeasonId, setSourceSeasonId] = useState('');
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [copyLoading, setCopyLoading] = useState(false);
    const [copyError, setCopyError] = useState(null);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [step]);

    useEffect(() => {
        if (!isOpen) return;
        setStep(1);
        setSeasonId('');
        setSettingsForm(emptySettingsForm());
        setSourceLeagueId('');
        setSourceSeasonId('');
        setSelectedTeams([]);
        setCopyError(null);
    }, [isOpen]);

    // Сезоны, которых у лиги ещё нет
    const availableSeasons = useMemo(() => {
        if (!league) return [];
        const taken = new Set(league.seasons.map(s => s.season_id));
        return seasonsCatalog.filter(s => !taken.has(s.season_id));
    }, [league, seasonsCatalog]);

    const selectedIds = useMemo(
        () => new Set(selectedTeams.map(t => t.team_id)),
        [selectedTeams]
    );

    // Копировать можно только из лиг, у которых сезоны вообще есть
    const leaguesWithSeasons = useMemo(
        () => (allLeagues || []).filter(l => l.seasons && l.seasons.length > 0),
        [allLeagues]
    );

    const sourceSeasons = useMemo(() => {
        const source = leaguesWithSeasons.find(l => String(l.id) === sourceLeagueId);
        return source ? source.seasons : [];
    }, [leaguesWithSeasons, sourceLeagueId]);

    if (!isOpen || !league) return null;

    const handleSourceLeague = (value) => {
        setSourceLeagueId(value);
        setSourceSeasonId('');
        setCopyError(null);
        setSelectedTeams([]);
    };

    const handleSourceSeason = async (value) => {
        setSourceSeasonId(value);
        setCopyError(null);
        if (value === '') {
            setSelectedTeams([]);
            return;
        }
        setCopyLoading(true);
        try {
            const teams = await resolveRoster(Number(sourceLeagueId), Number(value));
            setSelectedTeams((teams || []).map(normalizeTeam));
        } catch (e) {
            console.error('Ошибка копирования состава:', e);
            setCopyError('Не удалось загрузить состав выбранного сезона');
            setSelectedTeams([]);
        } finally {
            setCopyLoading(false);
        }
    };

    const toggleTeam = (team) => {
        setSelectedTeams(prev => prev.some(t => t.team_id === team.team_id)
            ? prev.filter(t => t.team_id !== team.team_id)
            : [...prev, team]);
    };

    const removeTeam = (teamId) => {
        setSelectedTeams(prev => prev.filter(t => t.team_id !== teamId));
    };

    const season = availableSeasons.find(s => String(s.season_id) === seasonId);
    const step1Valid = seasonId !== '' && isSettingsFormValid(settingsForm);

    const handleCreate = () => {
        if (!step1Valid) return;
        onCreate(season, settingsFormToPatch(settingsForm), selectedTeams);
        onClose();
    };

    return (
        <div className="modalOverlay" onClick={onClose}>
            <div className="modalContent modalContentWizard" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>Новый сезон в лиге «{league.name}»</h2>
                    <button className="modalClose" onClick={onClose}>×</button>
                </div>

                <div className="wizardSteps">
                    <div className={`wizardStep ${step === 1 ? 'wizardStepActive' : ''} ${step > 1 ? 'wizardStepDone' : ''}`}>
                        <span className="wizardStepNum">{step > 1 ? <FiCheck /> : 1}</span>
                        Сезон и настройки
                    </div>
                    <div className="wizardStepLine" />
                    <div className={`wizardStep ${step === 2 ? 'wizardStepActive' : ''}`}>
                        <span className="wizardStepNum">2</span>
                        Состав команд
                    </div>
                </div>

                <div className="modalBody modalBodyScroll" ref={bodyRef}>
                    {step === 1 ? (
                        <>
                            <div className="compactSection">
                                <h3 className="compactSectionTitle">Сезон</h3>
                                <div className="lmRow">
                                    <label className="modalLabel">Какой сезон добавляем</label>
                                    <select
                                        className="modalInput"
                                        value={seasonId}
                                        onChange={(e) => setSeasonId(e.target.value)}
                                    >
                                        <option value="">— выберите сезон —</option>
                                        {availableSeasons.map(s => (
                                            <option key={s.season_id} value={s.season_id}>
                                                {s.season_name}
                                            </option>
                                        ))}
                                    </select>
                                    {availableSeasons.length === 0 ? (
                                        <span className="fieldError">
                                            Все доступные сезоны уже заведены в этой лиге.
                                        </span>
                                    ) : (
                                        <span className="fieldHint">
                                            Показаны сезоны, которых ещё нет в этой лиге.
                                        </span>
                                    )}
                                </div>
                            </div>

                            <SeasonSettingsFields
                                form={settingsForm}
                                onChange={(field, value) =>
                                    setSettingsForm(prev => ({ ...prev, [field]: value }))}
                                applications={applications}
                                onCreateApplication={onCreateApplication}
                                compact
                            />
                        </>
                    ) : (
                        <>
                            <div className="compactSection">
                                <h3 className="compactSectionTitle">Откуда взять состав</h3>
                                <div className="lmRow">
                                    <label className="modalLabel">Лига</label>
                                    <select
                                        className="modalInput"
                                        value={sourceLeagueId}
                                        onChange={(e) => handleSourceLeague(e.target.value)}
                                    >
                                        <option value="">Начать с пустого состава</option>
                                        {leaguesWithSeasons.map(l => (
                                            <option key={l.id} value={l.id}>
                                                {l.name}{l.id === league.id ? ' (текущая)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="fieldHint">
                                        Можно взять состав из любой вашей лиги, не только из этой.
                                    </span>
                                </div>

                                {sourceLeagueId !== '' && (
                                    <div className="lmRow lmRowNested">
                                        <label className="modalLabel">Сезон</label>
                                        <select
                                            className="modalInput"
                                            value={sourceSeasonId}
                                            onChange={(e) => handleSourceSeason(e.target.value)}
                                        >
                                            <option value="">— выберите сезон —</option>
                                            {sourceSeasons.map(s => (
                                                <option key={s.season_id} value={s.season_id}>
                                                    {s.season_name}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="fieldHint">
                                            Скопированный состав можно править прямо здесь — убрать
                                            ненужные команды и добавить новые.
                                        </span>
                                        {copyError && <span className="fieldError">{copyError}</span>}
                                    </div>
                                )}
                            </div>

                            <div className="compactSection">
                                <h3 className="compactSectionTitle">
                                    Состав сезона
                                    <span className="teamsCount">{selectedTeams.length}</span>
                                </h3>
                                {copyLoading ? (
                                    <div className="teamsLoader">
                                        <Loader type="Oval" color="#667eea" height={32} width={32} />
                                    </div>
                                ) : selectedTeams.length === 0 ? (
                                    <p className="teamsEmpty">
                                        Пока пусто. Команды можно добавить сейчас или позже
                                        на странице сезона.
                                    </p>
                                ) : (
                                    <div className="selectedTeamsList">
                                        {selectedTeams.map(team => (
                                            <div key={team.team_id} className="selectedTeamChip">
                                                <img
                                                    src={resolveImage(team.image) || defaultTeamLogo}
                                                    alt={team.name}
                                                    onError={(e) => { e.target.src = defaultTeamLogo; }}
                                                />
                                                <span>{team.name}</span>
                                                <button
                                                    type="button"
                                                    className="chipRemove"
                                                    onClick={() => removeTeam(team.team_id)}
                                                    title="Убрать из сезона"
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="compactSection">
                                <h3 className="compactSectionTitle">Добавить команду</h3>
                                <TeamPicker
                                    allTeams={allTeams}
                                    loading={allTeamsLoading}
                                    isPicked={(teamId) => selectedIds.has(teamId)}
                                    onPick={toggleTeam}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="modalFooter">
                    {seasonId !== '' && !step1Valid && (
                        <span className="fieldError" style={{ marginRight: 'auto' }}>
                            {settingsFormError(settingsForm)}
                        </span>
                    )}
                    {step === 2 && (
                        <button
                            className="modalButton modalButtonCancel"
                            onClick={() => setStep(1)}
                        >
                            Назад
                        </button>
                    )}
                    <button className="modalButton modalButtonCancel" onClick={onClose}>
                        Отмена
                    </button>
                    {step === 1 ? (
                        <button
                            className="modalButton modalButtonSave"
                            onClick={() => setStep(2)}
                            disabled={!step1Valid}
                        >
                            Далее: состав
                        </button>
                    ) : (
                        <button
                            className="modalButton modalButtonSave"
                            onClick={handleCreate}
                            disabled={!step1Valid}
                        >
                            Создать сезон
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
